import React, { useEffect, useRef } from 'react';
import { View } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import { FormFieldComponent } from '../FormField';
import styles from './GroupComponentField.module.scss';

interface GroupComponentFieldProps {
  field: FormField;
  value: any;
  onChange: (fieldName: string, value: any) => void;
  formValues: Record<string, any>;
}

function isPhaseDateNA(groupValue: any): boolean {
  const dateValue = groupValue?.phaseDate;

  if (!dateValue) {
    return true;
  }

  if (dateValue.type === 'special' && dateValue.value === 'N/A') {
    return true;
  }

  if (dateValue === 'N/A' || dateValue === '') {
    return true;
  }

  return false;
}

const GroupLabelOutside: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required
}) => (
  <label className={styles.groupLabelOutside}>
    {label}
    {/* {required && <span className={styles.required}> *</span>} */}
  </label>
);

const GroupLabelInside: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required
}) => (
  <label className={styles.fieldLabel}>
    {label}
    {required && <span className={styles.required}> *</span>}
  </label>
);

interface GroupFieldsRowProps {
  fieldGroup: FormField[];
  groupValue: any;
  formValues: Record<string, any>;
  onNestedFieldChange: (fieldName: string, newValue: any) => void;
  labelOutside: boolean;
}

const GroupFieldsRow: React.FC<GroupFieldsRowProps> = ({
  fieldGroup,
  groupValue,
  formValues,
  onNestedFieldChange,
  labelOutside
}) => (
  <div
    className={
      labelOutside
        ? `${styles.fieldsRow} ${styles.fieldsRowWithOutsideLabel}`
        : styles.fieldsRow
    }
  >
    {fieldGroup.map((groupField: FormField) => {
      const shouldDisable =
        (groupField.name === 'tier' || groupField.name === 'event') &&
        isPhaseDateNA(groupValue);

      const finalDisabled =
        groupField.name === 'tier' || groupField.name === 'event'
          ? shouldDisable
          : groupField.disabled;

      const modifiedField = {
        ...groupField,
        disabled: finalDisabled
      };

      return (
        <div
          key={`${groupField.name}-${JSON.stringify(groupValue?.phaseDate)}`}
          className={styles.fieldWrapper}
        >
          <FormFieldComponent
            field={modifiedField}
            value={groupValue?.[groupField.name]}
            onChange={newValue =>
              onNestedFieldChange(groupField.name, newValue)
            }
            formValues={formValues}
          />
        </div>
      );
    })}
  </div>
);

export const GroupComponentField: React.FC<GroupComponentFieldProps> = ({
  field,
  value,
  onChange,
  formValues
}) => {
  const stateRef = useRef<Record<string, any>>(value || {});

  useEffect(() => {
    if (value && typeof value === 'object') {
      stateRef.current = { ...stateRef.current, ...value };
    }
  }, [value]);

  const handleFieldChange = (fieldName: string, newValue: any) => {
    const updatedValue = {
      ...stateRef.current,
      [fieldName]: newValue
    };

    stateRef.current = updatedValue;
    onChange(field.name, updatedValue);
  };

  useEffect(() => {
    // Ensures re-render when phase date changes (tier/event disabled state).
  }, [value?.phaseDate]);

  const displayLabelOutside = field.displayLabelOutside === true;
  const fieldGroup = field.fieldGroup ?? [];

  const groupBox = (
    <View UNSAFE_className={styles.groupComponentContainer}>
      {!displayLabelOutside && (
        <GroupLabelInside label={field.label} required={field.required} />
      )}
      <GroupFieldsRow
        fieldGroup={fieldGroup}
        groupValue={value}
        formValues={formValues}
        onNestedFieldChange={handleFieldChange}
        labelOutside={displayLabelOutside}
      />
    </View>
  );

  if (displayLabelOutside) {
    return (
      <div className={styles.outsideLabelLayout}>
        <GroupLabelOutside label={field.label} required={field.required} />
        {groupBox}
      </div>
    );
  }

  return groupBox;
};
